// Catalog.ID Client — Delegate Membership Kit assembly
// ============================================================================
// ONE builder for the delegate kit, used by BOTH wizards that produce one: the
// organization wizard prints it for the founding delegate, and the activation
// wizard prints it for an invited delegate (registration-restructure §13.3,
// M13). They printed slightly different documents before this module existed;
// a delegate must not be able to tell how they were enrolled by looking at
// their paper.
//
// The order mirrors the personal kit exactly:
//
//   cover → introduction (TOC + consent statement) → Delegate Credentials
//        → Account Unlock Sheet → XRPL Master Key → Authenticator Key
//        → Bitcash Wallet → Delegate Terms (appended)
//
// Every sheet after the credentials is conditional and drops out cleanly —
// page, TOC row and page count together:
//
//   * the Unlock Sheet needs an issued sealed token. Round 6 gives every
//     delegate one (they now verify an email and a phone claim, §13.3), but a
//     kit reprinted before the token exists, or a resume from an older stash,
//     simply prints without it rather than failing;
//   * the XRPL sheet needs an `xrpl_master` entry in the bag — the ceremony
//     mints one (§13.1), and a pre-round-6 resume without one prints without
//     the sheet;
//   * the Authenticator Key sheet (round 8, §15.6) needs a secret the NODE
//     already holds — the finalize helper reports that as `totpStored`, and a
//     resume from a pre-round-8 stash simply prints without it;
//   * the Bitcash Wallet sheet (round 10, registration-wallets §7) needs the
//     wallet the BAG holds — `finalizeCredentials(...).bitcashWallet.key`, never
//     the ceremony's value, since a resume adopts the wallet the bits were
//     already released into. It is now the last numbered sheet of the delegate
//     kit, and it prints without its address block when no receive address has
//     been minted yet (waived mode, or a resume that stopped short of §4.5's
//     address step).
//
// The agreement asset is passed in rather than imported: `terms-asset.ts` uses
// Vite `?url` imports, and this module has to stay importable from Node (unit
// tests and the sample renderer).

import type { Content } from 'pdfmake/interfaces';
import { buildCoverPage } from './cover-page';
import { buildFrontPage } from './front-page';
import { buildCredentialsPage } from './credentials-page';
import { buildResetCardPage } from './reset-card-page';
import { buildXrplKeyPage } from './xrpl-key-page';
import { buildAuthenticatorKeyPage } from './authenticator-key-page';
import { buildBitcashWalletPage, BITCASH_WALLET_PAGE_TITLE } from './bitcash-wallet-page';
import { buildKitLayout, type KitLayout } from './kit-layout';
import { bookletTitle, type AccountHeaderData } from './shared';
import type { BookletPrintOptions, CopySheet } from './booklet';
import { deriveXrplAddress, encodeXrplSeed } from '../../crypto/xrpl';

/** The pre-rendered Delegate Terms — structurally `DELEGATE_TERMS_ASSET`. */
export interface DelegateKitTerms {
    title: string;
    pages: number;
    url: string;
}

export interface DelegateKitData {
    /** Account header for every page: delegate cid, Organization / Domain /
     *  Role rows, version stamp. */
    header: AccountHeaderData;
    password: string;
    supersecret: string;
    /** The verified channels — the credentials page's Contact Channels section
     *  and the Unlock Sheet's Contact row (§13.3: the delegate credentials page
     *  gains the Contact row now that delegates verify both). */
    contact?: { email?: string; phone?: string };
    /** The validator-sealed Unlock Sheet token. Null/absent ⇒ no sheet. */
    unlockSheetToken?: Uint8Array | null;
    /** Where an unlock starts — from the API base, never `location.host` (§11). */
    nodeHost?: string;
    /** The 16 bytes the bag's `xrpl_master` entry actually holds. Null/absent
     *  ⇒ no XRPL sheet. */
    xrplSeedEntropy?: Uint8Array | null;
    /** The 20-byte Authenticator Key the NODE holds for this delegate (§15.6).
     *  Null/absent ⇒ no Authenticator Key sheet. Never a secret the node has
     *  not accepted: the sheet must print what a login will actually check. */
    totpSecret?: Uint8Array | null;
    /** The bitcash wallet key the BAG holds (`finalizeCredentials`'s
     *  `bitcashWallet.key`), compact or display form. Null/absent ⇒ no Bitcash
     *  Wallet sheet. */
    bitcashWalletKey?: string | null;
    /** The wallet's public receive address, when one has been minted (§4.5).
     *  Null/absent ⇒ the sheet prints without its address block. */
    bitcashReceiveAddress?: string | null;
    /** Whether the delegate's bits were released into that wallet before printing
     *  (the Wallet sheet's `bitsPaidIn`). */
    bitcashBitsPaidIn: boolean;
    /** The agreement appended at print time. Absent ⇒ a kit without terms. */
    terms?: DelegateKitTerms;
}

export interface DelegateKit {
    pages: Content[][];
    copySheets: CopySheet[];
    print: BookletPrintOptions;
    /** Exposed for callers that want the numbering (and for the tests). */
    layout: KitLayout;
}

export function buildDelegateKit(data: DelegateKitData): DelegateKit {
    const unlockToken = data.unlockSheetToken ?? null;
    const xrplEntropy = data.xrplSeedEntropy ?? null;
    const totpSecret = data.totpSecret ?? null;
    const bitcashWalletKey = data.bitcashWalletKey ?? null;

    const title = bookletTitle('delegate');
    const sections = [
        { title: 'Cover', pages: 1, inToc: false },
        { title: 'Introduction', pages: 1 },
        { title: 'Delegate Credentials', pages: 1 },
    ];
    if (unlockToken) sections.push({ title: 'Account Unlock Sheet', pages: 1, inToc: true });
    if (xrplEntropy) sections.push({ title: 'XRPL Master Key', pages: 1, inToc: true });
    if (totpSecret) sections.push({ title: 'Authenticator Key', pages: 1, inToc: true });
    if (bitcashWalletKey) sections.push({ title: BITCASH_WALLET_PAGE_TITLE, pages: 1, inToc: true });
    const layout = buildKitLayout({
        sections,
        terms: data.terms ? { title: data.terms.title, pages: data.terms.pages } : undefined,
    });

    const pages: Content[][] = [
        buildCoverPage({ title, header: data.header }),
        buildFrontPage({ layout }),
        buildCredentialsPage({
            header: data.header,
            password: data.password,
            supersecret: data.supersecret,
            email: data.contact?.email,
            phone: data.contact?.phone,
        }),
    ];
    if (unlockToken) {
        pages.push(buildResetCardPage({
            header: data.header,
            sealedToken: unlockToken,
            nodeHost: data.nodeHost,
            contact: data.contact,
        }));
    }
    if (xrplEntropy) {
        pages.push(buildXrplKeyPage({
            header: data.header,
            address: deriveXrplAddress(xrplEntropy),
            secret: encodeXrplSeed(xrplEntropy),
        }));
    }
    if (totpSecret) {
        pages.push(buildAuthenticatorKeyPage({ header: data.header, secret: totpSecret }));
    }
    if (bitcashWalletKey) {
        pages.push(buildBitcashWalletPage({
            header: data.header,
            walletKey: bitcashWalletKey,
            receiveAddress: data.bitcashReceiveAddress ?? null,
            bitsPaidIn: data.bitcashBitsPaidIn,
        }));
    }

    return {
        pages,
        copySheets: [],
        layout,
        print: {
            title,
            totalPages: layout.totalPages,
            coverArtwork: true,
            ...(data.terms ? { terms: { url: data.terms.url, pages: data.terms.pages } } : {}),
        },
    };
}
